import { ArrowDown, ArrowUp, ChevronsUpDown, Copy, Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import { cn } from './lib/cn'
import { useCopy, usePagination } from './lib/hooks'

/* ==========================================================================
 * Badge
 * ======================================================================== */

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'outline'

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'border-line bg-sunken text-muted',
  accent: 'border-accent/25 bg-accent-tint/20 text-accent',
  success: 'border-success/30 bg-success-soft text-success-text',
  warning: 'border-warning/30 bg-warning-soft text-warning-text',
  danger: 'border-danger/30 bg-danger-soft text-danger-text',
  info: 'border-info/30 bg-info-soft text-info-text',
  outline: 'border-line-strong bg-transparent text-muted',
}

export interface BadgeProps extends React.ComponentProps<'span'> {
  tone?: BadgeTone
  /** Leading dot, for status badges. */
  dot?: boolean
  size?: 'sm' | 'md'
}

export function Badge({
  tone = 'neutral',
  dot = false,
  size = 'md',
  className,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-caption',
        BADGE_TONES[tone],
        className
      )}
      {...props}
    >
      {dot ? <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden /> : null}
      {children}
    </span>
  )
}

/* ==========================================================================
 * StatusDot
 * ======================================================================== */

export type StatusTone = 'operational' | 'degraded' | 'partial' | 'outage' | 'unknown'

const STATUS_COLOURS: Record<StatusTone, string> = {
  operational: 'bg-success',
  degraded: 'bg-warning',
  partial: 'bg-warning',
  outage: 'bg-danger',
  unknown: 'bg-subtle',
}

const STATUS_TEXT: Record<StatusTone, string> = {
  operational: 'text-success-text',
  degraded: 'text-warning-text',
  partial: 'text-warning-text',
  outage: 'text-danger-text',
  unknown: 'text-subtle',
}

/**
 * A status indicator. Colour is never the only signal — the label is always
 * rendered (visually or for screen readers), per the accessibility rules.
 */
export interface StatusDotProps {
  tone: StatusTone
  label?: string
  className?: string
}

export function StatusDot({ tone, label, className }: StatusDotProps) {
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <span
        aria-hidden
        className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          STATUS_COLOURS[tone],
          tone !== 'unknown' && 'ring-4 ring-current/10'
        )}
      />
      {label ? (
        <span className={cn('text-caption font-medium', STATUS_TEXT[tone])}>{label}</span>
      ) : (
        <span className="sr-only">{tone}</span>
      )}
    </span>
  )
}

/* ==========================================================================
 * Copy affordances
 * ======================================================================== */

export interface CopyButtonProps {
  value: string
  label?: string
  className?: string
}

export function CopyButton({ value, label = 'Copy', className }: CopyButtonProps) {
  const { copied, copy } = useCopy()

  return (
    <button
      type="button"
      onClick={() => void copy(value)}
      aria-label={copied ? 'Copied' : label}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-caption font-medium transition-colors',
        copied ? 'text-success-text' : 'text-muted hover:bg-sunken hover:text-foreground',
        className
      )}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  )
}

export interface CopyFieldProps {
  value: string
  label?: React.ReactNode
  hint?: React.ReactNode
  className?: string
}

/** A monospace value with a copy button — IDs, prefixes, hostnames. */
export function CopyField({ value, label, hint, className }: CopyFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label ? <p className="text-caption font-medium text-foreground">{label}</p> : null}
      <div className="flex items-center gap-2 rounded-control border border-line bg-sunken px-3 py-2">
        <code className="min-w-0 flex-1 truncate font-mono text-caption text-foreground">
          {value}
        </code>
        <CopyButton value={value} label="" />
      </div>
      {hint ? <p className="text-caption text-muted">{hint}</p> : null}
    </div>
  )
}

/* ==========================================================================
 * CodeBlock
 * ======================================================================== */

export interface CodeBlockProps {
  code: string
  /** Shown as a corner label. Not a syntax-highlighting hint. */
  language?: string
  /** Command blocks get a $ prefix and a copy button. */
  variant?: 'plain' | 'command'
  className?: string
}

export function CodeBlock({ code, language, variant = 'plain', className }: CodeBlockProps) {
  const { copied, copy } = useCopy()

  return (
    <div className={cn('overflow-hidden rounded-card border border-line bg-sunken', className)}>
      <div className="flex items-center justify-between gap-3 border-b border-line px-3 py-1.5">
        <span className="font-mono text-[11px] uppercase tracking-widest text-subtle">
          {language ?? (variant === 'command' ? 'shell' : 'text')}
        </span>
        <button
          type="button"
          onClick={() => void copy(code)}
          aria-label={copied ? 'Copied' : 'Copy code'}
          className={cn(
            'rounded p-1 transition-colors',
            copied ? 'text-success-text' : 'text-subtle hover:bg-surface hover:text-foreground'
          )}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <pre className="ms-scroll overflow-x-auto px-3 py-3 text-caption leading-relaxed">
        <code>
          {variant === 'command' ? <span className="select-none text-subtle">$ </span> : null}
          {code}
        </code>
      </pre>
    </div>
  )
}

/* ==========================================================================
 * Timeline
 * ======================================================================== */

export interface TimelineEntry {
  id: string
  title: React.ReactNode
  description?: React.ReactNode
  timestamp: React.ReactNode
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info'
  /** Extra content revealed when the entry is expanded. */
  detail?: React.ReactNode
}

const TIMELINE_DOT: Record<NonNullable<TimelineEntry['tone']>, string> = {
  neutral: 'bg-subtle',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
}

export interface TimelineProps {
  entries: TimelineEntry[]
  className?: string
}

export function Timeline({ entries, className }: TimelineProps) {
  const [openId, setOpenId] = useState<string | null>(null)

  return (
    <ol className={cn('relative space-y-1', className)}>
      {entries.map((entry, index) => {
        const hasDetail = Boolean(entry.detail)
        const open = openId === entry.id

        return (
          <li key={entry.id} className="relative flex gap-3 pb-4 last:pb-0">
            {/* Connector */}
            {index < entries.length - 1 ? (
              <span aria-hidden className="absolute left-[6px] top-4 h-full w-px bg-line" />
            ) : null}

            <span
              aria-hidden
              className={cn(
                'relative z-10 mt-1.5 h-3 w-3 shrink-0 rounded-full ring-4 ring-background',
                TIMELINE_DOT[entry.tone ?? 'neutral']
              )}
            />

            <div className="min-w-0 flex-1">
              <button
                type="button"
                disabled={!hasDetail}
                onClick={() => setOpenId(open ? null : entry.id)}
                className={cn(
                  'flex w-full flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-left',
                  hasDetail && 'cursor-pointer'
                )}
              >
                <span className="text-sm font-medium text-foreground">{entry.title}</span>
                <span className="font-mono text-caption text-subtle">{entry.timestamp}</span>
              </button>

              {entry.description ? (
                <p className="mt-0.5 text-caption text-muted">{entry.description}</p>
              ) : null}

              {hasDetail && open ? (
                <div className="mt-2 rounded-lg border border-line bg-sunken p-3 text-caption text-muted">
                  {entry.detail}
                </div>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/* ==========================================================================
 * ActivityFeed
 * ======================================================================== */

export interface ActivityItem {
  id: string
  icon?: React.ReactNode
  title: React.ReactNode
  meta?: React.ReactNode
  timestamp: React.ReactNode
}

export interface ActivityFeedProps {
  items: ActivityItem[]
  className?: string
}

export function ActivityFeed({ items, className }: ActivityFeedProps) {
  return (
    <ul className={cn('divide-y divide-line', className)}>
      {items.map((item) => (
        <li key={item.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
          {item.icon ? (
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sunken text-muted [&_svg]:h-4 [&_svg]:w-4">
              {item.icon}
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground">{item.title}</p>
            {item.meta ? <p className="mt-0.5 text-caption text-muted">{item.meta}</p> : null}
          </div>
          <span className="shrink-0 whitespace-nowrap font-mono text-caption text-subtle">
            {item.timestamp}
          </span>
        </li>
      ))}
    </ul>
  )
}

/* ==========================================================================
 * Pagination
 * ======================================================================== */

export interface PaginationProps {
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  from?: number
  to?: number
  total?: number
  className?: string
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  from,
  to,
  total,
  className,
}: PaginationProps) {
  if (pageCount <= 1 && total === undefined) return null

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex flex-wrap items-center justify-between gap-3', className)}
    >
      <p className="text-caption text-muted">
        {total !== undefined && from !== undefined && to !== undefined ? (
          <>
            Showing <span className="font-mono text-foreground">{from}</span>–
            <span className="font-mono text-foreground">{to}</span> of{' '}
            <span className="font-mono text-foreground">{total}</span>
          </>
        ) : (
          <>
            Page <span className="font-mono text-foreground">{page}</span> of{' '}
            <span className="font-mono text-foreground">{pageCount}</span>
          </>
        )}
      </p>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="h-8 rounded-lg px-2.5 text-caption font-medium text-muted transition-colors hover:bg-sunken hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          className="h-8 rounded-lg px-2.5 text-caption font-medium text-muted transition-colors hover:bg-sunken hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </nav>
  )
}

/* ==========================================================================
 * DataTable
 * ======================================================================== */

export interface Column<T> {
  /** Stable identity for React keys and sort state. */
  key: string
  header: React.ReactNode
  cell: (row: T) => React.ReactNode
  /** Provide to make the column sortable. */
  sortValue?: (row: T) => string | number | null | undefined
  align?: 'left' | 'right' | 'center'
  /** Extra classes for the cell. */
  className?: string
}

export interface DataTableProps<T> {
  data: T[]
  columns: Column<T>[]
  getRowId: (row: T) => string
  onRowClick?: (row: T) => void
  loading?: boolean
  skeletonRows?: number
  empty?: React.ReactNode
  pageSize?: number
  /** Heading used on each stacked card below `md`. Falls back to the first column. */
  cardTitle?: (row: T) => React.ReactNode
  className?: string
}

type SortState = { key: string; direction: 'asc' | 'desc' } | null

/**
 * A table on `md` and up, and a stacked card list below it — rather than a
 * horizontally scrolling table, which is unusable on a phone.
 *
 * Sorting is driven by each column's optional `sortValue`; columns without one
 * are not sortable.
 */
export function DataTable<T>({
  data,
  columns,
  getRowId,
  onRowClick,
  loading = false,
  skeletonRows = 5,
  empty,
  pageSize,
  cardTitle,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>(null)

  const sorted = useMemo(() => {
    if (!sort) return data
    const column = columns.find((c) => c.key === sort.key)
    if (!column?.sortValue) return data

    const factor = sort.direction === 'asc' ? 1 : -1
    return [...data].sort((a, b) => {
      const av = column.sortValue?.(a)
      const bv = column.sortValue?.(b)
      if (av === bv) return 0
      // Blanks always sink, whichever way the column is sorted.
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor
      return String(av).localeCompare(String(bv)) * factor
    })
  }, [data, columns, sort])

  const pagination = usePagination(sorted, pageSize ?? (sorted.length || 1))
  const rows = pageSize ? pagination.pageItems : sorted

  const toggleSort = (key: string) => {
    setSort((current) => {
      if (current?.key !== key) return { key, direction: 'asc' }
      if (current.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const alignClass = (align: Column<T>['align']) =>
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'

  /* ---------- states ---------- */

  if (loading) {
    return (
      <div className={cn('space-y-2', className)} aria-busy>
        {Array.from({ length: skeletonRows }).map((_, index) => (
          <div key={index} className="h-11 animate-pulse rounded-lg bg-sunken" />
        ))}
      </div>
    )
  }

  if (data.length === 0) {
    return <>{empty ?? null}</>
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* ---------- md and up: a real table ---------- */}
      <div className="hidden overflow-hidden rounded-card border border-line bg-surface md:block">
        <div className="ms-scroll overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line bg-sunken/60">
                {columns.map((column) => {
                  const active = sort?.key === column.key
                  const SortIcon = !active
                    ? ChevronsUpDown
                    : sort.direction === 'asc'
                      ? ArrowUp
                      : ArrowDown

                  return (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={
                        active
                          ? sort.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                      className={cn(
                        'whitespace-nowrap px-4 py-2.5 text-caption font-medium uppercase tracking-widest text-subtle',
                        alignClass(column.align)
                      )}
                    >
                      {column.sortValue ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(column.key)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded transition-colors hover:text-foreground',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40',
                            active && 'text-foreground'
                          )}
                        >
                          {column.header}
                          <SortIcon className="h-3 w-3 shrink-0" />
                        </button>
                      ) : (
                        column.header
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={getRowId(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-line last:border-0',
                    onRowClick && 'cursor-pointer transition-colors hover:bg-sunken/60'
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn('px-4 py-3 align-top', alignClass(column.align), column.className)}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- below md: stacked cards ---------- */}
      <ul className="space-y-2 md:hidden">
        {rows.map((row) => (
          <li
            key={getRowId(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              'rounded-card border border-line bg-surface p-4',
              onRowClick && 'cursor-pointer transition-colors hover:bg-sunken/60'
            )}
          >
            {cardTitle ? (
              <div className="mb-3 border-b border-line pb-2 text-sm font-medium text-foreground">
                {cardTitle(row)}
              </div>
            ) : null}
            <dl className="space-y-1.5">
              {columns.map((column) => (
                <div key={column.key} className="flex items-start justify-between gap-4">
                  <dt className="shrink-0 text-caption uppercase tracking-widest text-subtle">
                    {column.header}
                  </dt>
                  <dd className="min-w-0 text-right text-sm text-foreground">{column.cell(row)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>

      {pageSize && pagination.pageCount > 1 ? (
        <Pagination
          page={pagination.page}
          pageCount={pagination.pageCount}
          onPageChange={pagination.setPage}
          from={pagination.from}
          to={pagination.to}
          total={pagination.total}
        />
      ) : null}
    </div>
  )
}
